/**
 * PUMA GeoJSON Service
 * Loads GeoJSON and creates comprehensive PUMA-to-county mapping
 * Uses the same matching logic as the map component
 */

// Cache for GeoJSON data and PUMA-to-county mapping
let pumaToCountyCache: Map<number, string> = new Map();
let isLoading = false;
let loadPromise: Promise<void> | null = null;

/**
 * Load GeoJSON and build PUMA-to-county mapping
 */
async function loadGeoJsonMapping(): Promise<Map<number, string>> {
  // Return cached mapping if available
  if (pumaToCountyCache.size > 0) {
    return pumaToCountyCache;
  }

  // If already loading, wait for that promise
  if (isLoading && loadPromise) {
    await loadPromise;
    return pumaToCountyCache;
  }

  // Start loading
  isLoading = true;
  loadPromise = (async () => {
    try {
      const response = await fetch(`/data/california_pumas_2023.geojson?t=${Date.now()}`);
      
      if (!response.ok) {
        throw new Error(`Failed to load GeoJSON: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Build PUMA-to-county mapping from GeoJSON
      const mapping = new Map<number, string>();
      
      let featuresWithCountyFP = 0;
      let featuresWithoutCountyFP = 0;
      
      data.features?.forEach((feature: any) => {
        if (!feature.properties) return;
        
        // Extract PUMA code from GeoJSON (same logic as map component)
        const geojsonPuma = feature.properties.PUMACE20 || feature.properties.PUMA || feature.properties.GEOID || '0';
        
        // Extract detailed name from NAMELSAD20 
        // For single-county PUMAs: "Los Angeles County--LA (Southwest/Marina del Rey PUMA"
        // For multi-county PUMAs: "Del Norte, Lassen, Modoc, Plumas & Siskiyou Counties PUMA"
        // We'll store the full detailed name including ALL counties for multi-county PUMAs
        const nameLsad = feature.properties.NAMELSAD20 || feature.properties.NAME;
        if (!nameLsad) {
          featuresWithoutCountyFP++;
          return;
        }
        
        // Remove " PUMA" or " Counties PUMA" or " County PUMA" suffix but keep the full detailed name
        // For multi-county PUMAs, this preserves all counties listed: "Del Norte, Lassen, Modoc, Plumas & Siskiyou"
        // For single-county PUMAs, this preserves the full description: "Los Angeles County--LA (Southwest/Marina del Rey"
        let countyName = nameLsad.replace(/\s*(Counties|County)?\s*PUMA\s*$/i, '').trim();
        
        // For multi-county PUMAs, ensure all counties are included
        // If it contains commas or "&", it's a multi-county PUMA - keep all counties
        // If it's a single county with detailed location info, keep that too
        // The format might be: "County1, County2, County3 & County4" or "County--Location Info"
        
        // Clean up (remove trailing dashes or spaces) but preserve commas and & for multi-county
        countyName = countyName.replace(/[-\s]+$/, '').trim();
        
        // If we have a multi-county format (contains comma or &), format it nicely
        if (countyName.includes(',') || countyName.includes('&')) {
          // Replace " & " with ", " and then join with ", " to standardize
          // This ensures consistent formatting like "County1, County2, County3, County4"
          countyName = countyName.replace(/\s*&\s*/g, ', ').replace(/\s*,\s*/g, ', ').trim();
          // Remove any duplicate spaces after formatting
          countyName = countyName.replace(/\s+/g, ' ');
        }
        
        if (!countyName || countyName.length === 0) {
          featuresWithoutCountyFP++;
          return;
        }
        
        featuresWithCountyFP++;
        
        // Store multiple variations of PUMA codes (same strategies as map component)
        // Strategy 1: Direct parse (5-digit string like "00101" -> 101)
        const asInt = parseInt(geojsonPuma);
        if (!isNaN(asInt)) {
          mapping.set(asInt, countyName);
        }
        
        // Strategy 2: Remove leading zeros (e.g., "00101" -> 101)
        const withoutLeadingZeros = parseInt(geojsonPuma.replace(/^0+/, ''));
        if (!isNaN(withoutLeadingZeros)) {
          mapping.set(withoutLeadingZeros, countyName);
          
          // If it's different from asInt, also store variations
          if (withoutLeadingZeros !== asInt) {
            // Also store the original parsed value
            mapping.set(asInt, countyName);
          }
        }
        
        // Strategy 3: Store padded version if original was 5 digits
        // This ensures we can match both "101" and "00101" formats
        if (!isNaN(withoutLeadingZeros)) {
          const padded5String = withoutLeadingZeros.toString().padStart(5, '0');
          const padded5Int = parseInt(padded5String);
          if (padded5Int !== withoutLeadingZeros) {
            mapping.set(padded5Int, countyName);
          }
        }
      });
      
      pumaToCountyCache = mapping;
      
    } catch (error) {
      console.error('❌ Error loading GeoJSON for county mapping:', error);
      // Return empty cache on error
    } finally {
      isLoading = false;
      loadPromise = null;
    }
  })();

  await loadPromise;
  return pumaToCountyCache;
}

/**
 * Get county from PUMA code using GeoJSON mapping
 * Uses the same matching strategies as the map component
 */
export async function getCountyFromPumaGeoJson(pumaCode: number): Promise<string | null> {
  const mapping = await loadGeoJsonMapping();
  
  // Strategy 1: Direct lookup
  if (mapping.has(pumaCode)) {
    return mapping.get(pumaCode)!;
  }
  
  // Strategy 2: Try with leading zeros padded (e.g., 101 -> 00101 -> 101)
  const padded5 = pumaCode.toString().padStart(5, '0');
  const paddedInt = parseInt(padded5);
  if (mapping.has(paddedInt)) {
    return mapping.get(paddedInt)!;
  }
  
  // Strategy 3: Try without leading zeros (e.g., if pumaCode is 00101 as number, try 101)
  const withoutZeros = parseInt(pumaCode.toString().replace(/^0+/, ''));
  if (mapping.has(withoutZeros)) {
    return mapping.get(withoutZeros)!;
  }
  
  // Strategy 4: Try as string match (handle cases where GeoJSON has string format)
  // Check if any key in mapping matches when both are normalized
  const entries = Array.from(mapping.entries());
  for (const [key, value] of entries) {
    // Normalize both codes for comparison
    const keyNormalized = parseInt(key.toString().replace(/^0+/, ''));
    const codeNormalized = parseInt(pumaCode.toString().replace(/^0+/, ''));
    
    if (keyNormalized === codeNormalized) {
      return value;
    }
  }
  
  return null;
}

/**
 * Synchronous version - use cached mapping if available
 * Falls back to null if not loaded yet
 * Uses the same matching strategies as the async version
 */
export function getCountyFromPumaGeoJsonSync(pumaCode: number): string | null {
  if (pumaToCountyCache.size === 0) {
    // Trigger async load if not already loading
    if (!isLoading) {
      loadGeoJsonMapping();
    }
    return null;
  }
  
  // Strategy 1: Direct lookup
  if (pumaToCountyCache.has(pumaCode)) {
    return pumaToCountyCache.get(pumaCode)!;
  }
  
  // Strategy 2: Try with leading zeros padded (e.g., 101 -> 00101 -> 101)
  const padded5 = pumaCode.toString().padStart(5, '0');
  const paddedInt = parseInt(padded5);
  if (pumaToCountyCache.has(paddedInt)) {
    return pumaToCountyCache.get(paddedInt)!;
  }
  
  // Strategy 3: Try without leading zeros
  const withoutZeros = parseInt(pumaCode.toString().replace(/^0+/, ''));
  if (pumaToCountyCache.has(withoutZeros)) {
    return pumaToCountyCache.get(withoutZeros)!;
  }
  
  // Strategy 4: Try as normalized match (handle string/number variations)
  const entries = Array.from(pumaToCountyCache.entries());
  for (const [key, value] of entries) {
    // Normalize both codes for comparison
    const keyNormalized = parseInt(key.toString().replace(/^0+/, ''));
    const codeNormalized = parseInt(pumaCode.toString().replace(/^0+/, ''));
    
    if (keyNormalized === codeNormalized) {
      return value;
    }
  }
  
  return null;
}

/**
 * Pre-load GeoJSON mapping (call this early in app lifecycle)
 */
export function preloadGeoJsonMapping(): void {
  if (!isLoading && pumaToCountyCache.size === 0) {
    loadGeoJsonMapping();
  }
}

